import { DetectionFilterCondition, FilterField } from 'Generated/schema';

export enum FieldFilterTypes {
  STRING = 'string',
  INT = 'int',
  FLOAT = 'float',
  BOOL = 'bool',
  IP = 'ip',
  STRING_ARRAY = 'string_array',
  INT_ARRAY = 'int_array',
  FLOAT_ARRAY = 'float_array',
  BOOL_ARRAY = 'bool_array',
  IP_ARRAY = 'ip_array',
  JSON = 'json',
  JSON_ARRAY = 'json_array',
  // Frontend only
  CIDR = 'cidr',
}

export interface FilterFieldWithGroupName extends FilterField {
  groupName: string;
}

export type ExtractedFilterCondition = Omit<DetectionFilterCondition, 'value'> & {
  value?: Array<string> | Array<number> | Array<boolean> | string | number | null | boolean;
};
export type ExtractedFilterConditions = Array<ExtractedFilterCondition>;

export const PANTHER_MANAGED_LABEL = 'Panther Managed';
export const SHARED_LABEL = 'Shared';

export const SDLOperators = {
  Equals: {
    label: 'is',
    value: '==',
    fieldTypes: [
      FieldFilterTypes.BOOL,
      FieldFilterTypes.IP,
      FieldFilterTypes.INT,
      FieldFilterTypes.STRING,
    ],
    inputTypes: {
      [FieldFilterTypes.BOOL]: FieldFilterTypes.BOOL,
      [FieldFilterTypes.IP]: FieldFilterTypes.IP,
      [FieldFilterTypes.INT]: FieldFilterTypes.INT,
      [FieldFilterTypes.STRING]: FieldFilterTypes.STRING,
      default: FieldFilterTypes.STRING,
    },
  },
  DoesNotEqual: {
    label: 'is not',
    value: '!=',
    fieldTypes: [
      FieldFilterTypes.BOOL,
      FieldFilterTypes.IP,
      FieldFilterTypes.INT,
      FieldFilterTypes.STRING,
    ],
    inputTypes: {
      [FieldFilterTypes.BOOL]: FieldFilterTypes.BOOL,
      [FieldFilterTypes.IP]: FieldFilterTypes.IP,
      [FieldFilterTypes.INT]: FieldFilterTypes.INT,
      [FieldFilterTypes.STRING]: FieldFilterTypes.STRING,
      default: FieldFilterTypes.STRING,
    },
  },
  IsIn: {
    label: 'is in',
    value: 'in',
    fieldTypes: [FieldFilterTypes.INT, FieldFilterTypes.STRING],
    inputTypes: {
      [FieldFilterTypes.INT]: FieldFilterTypes.INT_ARRAY,
      [FieldFilterTypes.STRING]: FieldFilterTypes.STRING_ARRAY,
      default: FieldFilterTypes.STRING_ARRAY,
    },
  },
  IsNotIn: {
    label: 'is not in',
    value: '!in',
    fieldTypes: [FieldFilterTypes.INT, FieldFilterTypes.STRING],
    inputTypes: {
      [FieldFilterTypes.INT]: FieldFilterTypes.INT_ARRAY,
      [FieldFilterTypes.STRING]: FieldFilterTypes.STRING_ARRAY,
      default: FieldFilterTypes.STRING_ARRAY,
    },
  },
  IsIPAddressInCIDR: {
    label: 'is in CIDR',
    value: 'in_cidr',
    fieldTypes: [FieldFilterTypes.IP],
    inputTypes: { [FieldFilterTypes.IP]: FieldFilterTypes.CIDR, default: FieldFilterTypes.CIDR },
  },
  IsIPAddressNotInCIDR: {
    label: 'is not in CIDR',
    value: '!in_cidr',
    fieldTypes: [FieldFilterTypes.IP],
    inputTypes: { [FieldFilterTypes.IP]: FieldFilterTypes.CIDR, default: FieldFilterTypes.CIDR },
  },
  IsIPAddressPublic: {
    label: 'is public',
    value: 'is_public',
    fieldTypes: [FieldFilterTypes.IP],
    inputTypes: [],
  },
  IsIPAddressPrivate: {
    label: 'is private',
    value: 'is_private',
    fieldTypes: [FieldFilterTypes.IP],
    inputTypes: [],
  },
  IsNullOrEmpty: {
    label: 'is empty',
    value: 'empty',
    fieldTypes: [
      FieldFilterTypes.INT_ARRAY,
      FieldFilterTypes.FLOAT_ARRAY,
      FieldFilterTypes.IP_ARRAY,
      FieldFilterTypes.BOOL_ARRAY,
      FieldFilterTypes.STRING_ARRAY,
      FieldFilterTypes.STRING,
    ],
    inputTypes: {},
  },
  IsNotNullOrEmpty: {
    label: 'is not empty',
    value: '!empty',
    fieldTypes: [
      FieldFilterTypes.INT_ARRAY,
      FieldFilterTypes.FLOAT_ARRAY,
      FieldFilterTypes.IP_ARRAY,
      FieldFilterTypes.BOOL_ARRAY,
      FieldFilterTypes.STRING_ARRAY,
      FieldFilterTypes.STRING,
    ],
    inputTypes: {},
  },
  Contains: {
    label: 'contains',
    value: 'contains',
    fieldTypes: [
      FieldFilterTypes.INT_ARRAY,
      FieldFilterTypes.IP_ARRAY,
      FieldFilterTypes.BOOL_ARRAY,
      FieldFilterTypes.STRING_ARRAY,
      FieldFilterTypes.STRING,
    ],
    inputTypes: {
      [FieldFilterTypes.BOOL_ARRAY]: FieldFilterTypes.BOOL,
      [FieldFilterTypes.IP_ARRAY]: FieldFilterTypes.IP,
      [FieldFilterTypes.INT_ARRAY]: FieldFilterTypes.INT,
      [FieldFilterTypes.STRING_ARRAY]: FieldFilterTypes.STRING,
      [FieldFilterTypes.STRING]: FieldFilterTypes.STRING,
      default: FieldFilterTypes.STRING,
    },
  },
  DoesNotContain: {
    label: 'does not contain',
    value: '!contains',
    fieldTypes: [
      FieldFilterTypes.INT_ARRAY,
      FieldFilterTypes.IP_ARRAY,
      FieldFilterTypes.BOOL_ARRAY,
      FieldFilterTypes.STRING_ARRAY,
      FieldFilterTypes.STRING,
    ],
    inputTypes: {
      [FieldFilterTypes.BOOL_ARRAY]: FieldFilterTypes.BOOL,
      [FieldFilterTypes.IP_ARRAY]: FieldFilterTypes.IP,
      [FieldFilterTypes.INT_ARRAY]: FieldFilterTypes.INT,
      [FieldFilterTypes.STRING_ARRAY]: FieldFilterTypes.STRING,
      [FieldFilterTypes.STRING]: FieldFilterTypes.STRING,
      default: FieldFilterTypes.STRING,
    },
  },
  StartsWith: {
    label: 'starts with',
    value: 'starts_with',
    fieldTypes: [FieldFilterTypes.STRING],
    inputTypes: {
      [FieldFilterTypes.STRING]: FieldFilterTypes.STRING,
      default: FieldFilterTypes.STRING,
    },
  },
  DoesNotStartWith: {
    label: 'does not start with',
    value: '!starts_with',
    fieldTypes: [FieldFilterTypes.STRING],
    inputTypes: {
      [FieldFilterTypes.STRING]: FieldFilterTypes.STRING,
      default: FieldFilterTypes.STRING,
    },
  },
  EndsWith: {
    label: 'ends with',
    value: 'ends_with',
    fieldTypes: [FieldFilterTypes.STRING],
    inputTypes: {
      [FieldFilterTypes.STRING]: FieldFilterTypes.STRING,
      default: FieldFilterTypes.STRING,
    },
  },
  DoesNotEndWith: {
    label: 'does not end with',
    value: '!ends_with',
    fieldTypes: [FieldFilterTypes.STRING],
    inputTypes: {
      [FieldFilterTypes.STRING]: FieldFilterTypes.STRING,
      default: FieldFilterTypes.STRING,
    },
  },
  IsGreaterThan: {
    label: 'is greater than',
    value: '>',
    fieldTypes: [FieldFilterTypes.INT, FieldFilterTypes.FLOAT],
    inputTypes: {
      [FieldFilterTypes.INT]: FieldFilterTypes.INT,
      [FieldFilterTypes.FLOAT]: FieldFilterTypes.FLOAT,
      default: FieldFilterTypes.FLOAT,
    },
  },
  IsGreaterThanOrEquals: {
    label: 'is greater than or equal to',
    value: '>=',
    fieldTypes: [FieldFilterTypes.INT],
    inputTypes: {
      [FieldFilterTypes.INT]: FieldFilterTypes.INT,
      default: FieldFilterTypes.INT,
    },
  },
  IsLessThan: {
    label: 'is less than',
    value: '<',
    fieldTypes: [FieldFilterTypes.INT, FieldFilterTypes.FLOAT],
    inputTypes: {
      [FieldFilterTypes.INT]: FieldFilterTypes.INT,
      [FieldFilterTypes.FLOAT]: FieldFilterTypes.FLOAT,
      default: FieldFilterTypes.FLOAT,
    },
  },
  IsLessThanOrEquals: {
    label: 'is less than or equal to',
    value: '<=',
    fieldTypes: [FieldFilterTypes.INT],
    inputTypes: {
      [FieldFilterTypes.INT]: FieldFilterTypes.INT,
      default: FieldFilterTypes.INT,
    },
  },
  CIDRContainsIPAddresses: {
    label: 'contains IP in CIDR',
    value: 'contains_ip',
    fieldTypes: [FieldFilterTypes.IP_ARRAY],
    inputTypes: {
      [FieldFilterTypes.IP_ARRAY]: FieldFilterTypes.CIDR,
      default: FieldFilterTypes.CIDR,
    },
  },
  CIDRDoesNotContainIPAddresses: {
    label: 'does not contain IP in CIDR',
    value: '!contains_ip',
    fieldTypes: [FieldFilterTypes.IP_ARRAY],
    inputTypes: {
      [FieldFilterTypes.IP_ARRAY]: FieldFilterTypes.CIDR,
      default: FieldFilterTypes.CIDR,
    },
  },
  InsensitiveEquals: {
    label: 'is (case insensitive)',
    value: 'insensitive_equals',
    fieldTypes: [FieldFilterTypes.STRING],
    inputTypes: {
      [FieldFilterTypes.STRING]: FieldFilterTypes.STRING,
      default: FieldFilterTypes.STRING,
    },
  },
  InsensitiveDoesNotEqual: {
    label: 'is not (case insensitive)',
    value: '!insensitive_equals',
    fieldTypes: [FieldFilterTypes.STRING],
    inputTypes: {
      [FieldFilterTypes.STRING]: FieldFilterTypes.STRING,
      default: FieldFilterTypes.STRING,
    },
  },
  InsensitiveStartsWith: {
    label: 'starts with (case insensitive)',
    value: 'insensitive_starts_with',
    fieldTypes: [FieldFilterTypes.STRING],
    inputTypes: {
      [FieldFilterTypes.STRING]: FieldFilterTypes.STRING,
      default: FieldFilterTypes.STRING,
    },
  },
  InsensitiveDoesNotStartWith: {
    label: 'does not start with (case insensitive)',
    value: '!insensitive_starts_with',
    fieldTypes: [FieldFilterTypes.STRING],
    inputTypes: {
      [FieldFilterTypes.STRING]: FieldFilterTypes.STRING,
      default: FieldFilterTypes.STRING,
    },
  },
  InsensitiveEndsWith: {
    label: 'ends with (case insensitive)',
    value: 'insensitive_ends_with',
    fieldTypes: [FieldFilterTypes.STRING],
    inputTypes: {
      [FieldFilterTypes.STRING]: FieldFilterTypes.STRING,
      default: FieldFilterTypes.STRING,
    },
  },
  InsensitiveDoesNotEndWith: {
    label: 'does not end with (case insensitive)',
    value: '!insensitive_ends_with',
    fieldTypes: [FieldFilterTypes.STRING],
    inputTypes: {
      [FieldFilterTypes.STRING]: FieldFilterTypes.STRING,
      default: FieldFilterTypes.STRING,
    },
  },
  InsensitiveContains: {
    label: 'contains (case insensitive)',
    value: 'insensitive_contains',
    fieldTypes: [FieldFilterTypes.STRING_ARRAY, FieldFilterTypes.STRING],
    inputTypes: {
      [FieldFilterTypes.STRING_ARRAY]: FieldFilterTypes.STRING,
      [FieldFilterTypes.STRING]: FieldFilterTypes.STRING,
      default: FieldFilterTypes.STRING,
    },
  },
  InsensitiveDoesNotContain: {
    label: 'does not contain (case insensitive)',
    value: '!insensitive_contains',
    fieldTypes: [FieldFilterTypes.STRING_ARRAY, FieldFilterTypes.STRING],
    inputTypes: {
      [FieldFilterTypes.STRING_ARRAY]: FieldFilterTypes.STRING,
      [FieldFilterTypes.STRING]: FieldFilterTypes.STRING,
      default: FieldFilterTypes.STRING,
    },
  },
};

export const FieldFilterOperators = [
  // Equality operators
  SDLOperators.Equals,
  SDLOperators.InsensitiveEquals,
  SDLOperators.DoesNotEqual,
  SDLOperators.InsensitiveDoesNotEqual,

  // List/Set operators
  SDLOperators.IsIn,
  SDLOperators.IsNotIn,

  // String operators
  SDLOperators.Contains,
  SDLOperators.InsensitiveContains,
  SDLOperators.DoesNotContain,
  SDLOperators.InsensitiveDoesNotContain,
  SDLOperators.StartsWith,
  SDLOperators.InsensitiveStartsWith,
  SDLOperators.DoesNotStartWith,
  SDLOperators.InsensitiveDoesNotStartWith,
  SDLOperators.EndsWith,
  SDLOperators.InsensitiveEndsWith,
  SDLOperators.DoesNotEndWith,
  SDLOperators.InsensitiveDoesNotEndWith,

  // Numeric comparison operators
  SDLOperators.IsGreaterThan,
  SDLOperators.IsGreaterThanOrEquals,
  SDLOperators.IsLessThan,
  SDLOperators.IsLessThanOrEquals,

  // Existence operators
  SDLOperators.IsNullOrEmpty,
  SDLOperators.IsNotNullOrEmpty,

  // IP/Network operators
  SDLOperators.IsIPAddressPublic,
  SDLOperators.IsIPAddressPrivate,
  SDLOperators.IsIPAddressInCIDR,
  SDLOperators.IsIPAddressNotInCIDR,
  SDLOperators.CIDRContainsIPAddresses,
  SDLOperators.CIDRDoesNotContainIPAddresses,
];
